import { useEffect, useMemo, useState } from 'react';
import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';
import { useFetchSmartLinkThemesQuery } from '../../../../redux/api/fymAPI';

/** How many themes the Look tab shows before "All 14 themes" (design smartlink-editor a5). */
export const FEATURED_THEME_COUNT = 8;

const ALL_THEMES = Object.values(SerializedSmartLinkVersionTheme);

/** The themes this account reaches for most, then the rest in enum order. */
export function rankThemes(usage: readonly SerializedSmartLinkVersionTheme[]): SerializedSmartLinkVersionTheme[] {
  const ranked: SerializedSmartLinkVersionTheme[] = [];
  [...usage, ...ALL_THEMES].forEach((theme) => {
    if (ranked.length < FEATURED_THEME_COUNT && !ranked.includes(theme)) {
      ranked.push(theme);
    }
  });
  return ranked;
}

/**
 * Puts the current theme in the list without moving the others: a theme in the
 * list keeps its place, a theme from "All themes" takes the last slot.
 */
export function withCurrentTheme(
  ranked: readonly SerializedSmartLinkVersionTheme[],
  currentTheme: SerializedSmartLinkVersionTheme
): SerializedSmartLinkVersionTheme[] {
  if (ranked.includes(currentTheme)) {
    return [...ranked];
  }
  return [...ranked.slice(0, FEATURED_THEME_COUNT - 1), currentTheme];
}

/**
 * The eight themes to show in the Look tab: the ones this account reaches for most,
 * with the page's current theme always among them so the selection is visible
 * without opening the modal.
 *
 * The order is fixed per account at the first fresh read of the usage: a pick
 * (which also changes the usage counts) never moves the tiles under the pointer.
 */
export function useFeaturedThemes(
  accountId: number,
  currentTheme: SerializedSmartLinkVersionTheme
): SerializedSmartLinkVersionTheme[] {
  const { data, isFetching } = useFetchSmartLinkThemesQuery({ accountId });
  const [frozen, setFrozen] = useState<{ accountId: number; ranked: SerializedSmartLinkVersionTheme[] } | null>(null);

  const live = useMemo(() => rankThemes((data?.themes ?? []).map((usage) => usage.theme)), [data]);
  const isFrozen = frozen !== null && frozen.accountId === accountId;

  useEffect(() => {
    // A cached answer can be stale; wait for the read to end before the order is fixed.
    if (isFrozen || isFetching || data === undefined) {
      return;
    }
    setFrozen({ accountId, ranked: live });
  }, [isFrozen, isFetching, data, accountId, live]);

  const ranked = isFrozen ? frozen.ranked : live;
  return useMemo(() => withCurrentTheme(ranked, currentTheme), [ranked, currentTheme]);
}
